/**
 * provides abstract functions for making a 'POST' or 'GET' request to an api.
 * Following calls can be made:
 * - NGXAjax.get(url, getParams, config) - will make a 'GET' request to the api, appending 'getParams' to the url
 * - NGXAjax.post(url, postParams, getParams, config) - will make a 'POST' request to the api, sending 'postParams' in the request body and appending 'getParams' to the url
 * - NGXAjax.delete(url)
 */

import axios from 'axios';
import _ from 'lodash';
import logger from './logger';
import { ApiUtils } from '../api';

const ajaxInstance = axios.create({
  baseURL:         App.globalContext.serverURL,
  timeout:         30000,
  withCredentials: true, // Issue CORS requests with credentials
  headers:         {
    'X-Requested-With': 'XMLHttpRequest',
  },
});

const heliosInstance = axios.create({
  baseURL:         `${App.globalContext.heliosURL}/api/interact/m/`,
  timeout:         30000,
  withCredentials: true,
  headers:         {
    'Access-Control-Allow-Origin': App.globalContext.serverURLsecure,
  },
});

/**
 * HERCULES 2
 */
const herculesInstance = axios.create({
  baseURL:         `${App.globalContext.herculesURL}/api/export/m/`,
  timeout:         30000,
  withCredentials: true,
  headers:         {
    'Access-Control-Allow-Origin': App.globalContext.serverURLsecure,
  },
});

/**
 * CRONOS
 */
const cronosInstance = axios.create({
  baseURL:         `${App.globalContext.cronosURLSecure}`,
  timeout:         30000,
  withCredentials: true,
  headers:         {
    'Access-Control-Allow-Origin': App.globalContext.serverURLsecure,
  },
});

/**
 * ANALYTICS
 * NOTE: Please don't apply our standard response interceptor (responseInterceptor) to AnalyticsInstance,
 * because it tracks 401 errors and does unnecessary requests to get a new JWT token, when ANX uses their own JWT token.
 */
const analyticsInstance = axios.create({
  timeout:         30000,
  withCredentials: true,
});

const requestInterceptor = (config) => _getToken().then((token) => _addAuthHeader(config, token));
const responseInterceptor = ((response) => response, _retryExpiredJWTInterceptor);

// Ensure all requests have a JWT and set the appropriate headers
heliosInstance.interceptors.request.use(requestInterceptor);

// Apply retry handler for any 401 responses
heliosInstance.interceptors.response.use((response) => {
  if (response.data.hasOwnProperty('code')) response.data.statuscode = response.data.code; // helios uses 'code' rather than 'statuscode' so lets add it
  return response;
}, responseInterceptor);

// Validate all responses so callers don't need to
// NOTE: the order here matters, this must be applied after _retryExpiredJWTInterceptor
heliosInstance.interceptors.response.use(ApiUtils.validateHelios, ApiUtils.validateHelios);

// Ensure all requests have a JWT and set the appropriate headers
herculesInstance.interceptors.request.use(requestInterceptor);
// Apply retry handler for any 401 responses
herculesInstance.interceptors.response.use((response) => response, responseInterceptor);
// Validate all responses so callers don't need to
// NOTE: the order here matters, this must be applied after _retryExpiredJWTInterceptor
herculesInstance.interceptors.response.use(ApiUtils.validate, ApiUtils.validate);

// Ensure all requests have a JWT and set the appropriate headers
cronosInstance.interceptors.request.use(requestInterceptor);
// Apply retry handler for any 401 responses
cronosInstance.interceptors.response.use((response) => response, responseInterceptor);
// Validate all responses so callers don't need to
// NOTE: the order here matters, this must be applied after _retryExpiredJWTInterceptor
cronosInstance.interceptors.response.use(ApiUtils.validateCronos, ApiUtils.validateCronos);

// Logic to reload a page, when receive 401 from the server (invalid session)
const unathorizedResponseInterceptor =
  ((response) => response,
  (response) => {
    if (_isUnauthorized(response.toJSON())) {
      App.reloadExpiredSession();
    }
    return response;
  });
ajaxInstance.interceptors.response.use((response) => response, unathorizedResponseInterceptor);

const _NGXAjax = _createRestApi(ajaxInstance);
const _heliosAjax = _createRestApi(heliosInstance);
const _herculesAjax = _createRestApi(herculesInstance);
const _cronosAjax = _createRestApi(cronosInstance);
const _analyticsAjax = _createRestApi(analyticsInstance);

_NGXAjax.helios = _heliosAjax;
_NGXAjax.hercules = _herculesAjax;
_NGXAjax.cronos = _cronosAjax;
_NGXAjax.analytics = _analyticsAjax;

/**
 * This interceptor specially handles 401 responses which would be an indication that the JWT is invalid
 * It will make an attempt to refresh the JWT and retry the request
 * @param {object} response - the axios response
 */
function _retryExpiredJWTInterceptor(response) {
  if (_isUnauthorized(response)) {
    logger.info('JWT has expired, attempting to refresh');

    return _getToken({ forceRefresh: true })
      .then(() => {
        const originalRequest = response.config;
        logger.info(`JWT refreshed. Attempting to retry failed request to ${originalRequest.url}`);

        return axios(originalRequest)
          .then((response) => {
            logger.info('Intial request successfully retried');
            return response;
          })
          .catch((error) => {
            logger.error('Retry failed');
            return error;
          });
      })
      .catch((error) => {
        if (_isUnauthorized(response)) {
          logger.error('JWT refresh failed.');
        }
        return error;
      });
  } else {
    return response;
  }
}

function _isUnauthorized(response) {
  return response.status === 401;
}

/**
 * abstracts out the request calls to what ever ajax supplier module you want. in our case, for now, that's axios
 * @param      {string}     method     PUT, POST, GET, DELETE
 * @param      {string}     url        the api endpoint
 * @param      {object}     getParams  the data to pass with the ajax call as 'GET' params, appended in the url
 * @param      {object}     postParams the data to pass with the ajax call as 'POST' params added to the 'POST' request
 * @param      {object}     opts       override options in the ajax call - you can use this to override like this:
 * Override headers:
 * NGXAjax.post('/path.json', postParams, {
 *    headers: {
 *      'My-Header-Override: custom value'
 *    }
 * })
 *
 * Add custom headers
 * NGXAjax.post('/path.json', postParams, {
 *    headers: {
 *      'My-Header-Override: custom value',
*       'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
 *    }
 * })
 *
 * `postJSON` boolean can be set as shorthand to POST with header Content-Type 'application/json'
 * NGXAjax.post('/path.json', postParams, {
 *   postJSON: true
 * })
 *
 * Override method - if you really have to :)
 * NGXAjax.post('/path.json', postParams, {
 *    method: 'GET'
 * })
 *
 * Transform response
 *
 * NGXAjax.post('/path.json', postParams, {
 *   transformResponse: [function (data) {
        // Do whatever you want to transform the data
        return data;
      }]
 * })
 *
 */
function _createRequest(method, url, params = {}, data = {}, opts = {}) {
  if (!method) {
    throw new TypeError('Parameter "method" cannot be undefined');
  }

  if (!url) {
    throw new TypeError('Paramter "url" cannot be undefined');
  }

  const isPostMethod = method.toUpperCase() === 'POST';
  const isGetMethod = method.toUpperCase() === 'GET';
  const defaults = {
    responseType: 'json',
    headers:      {
      'Content-Type':
        isPostMethod && opts.postJSON
          ? 'application/json'
          : isGetMethod
            ? ''
            : 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    params:           { rnd: new Date().getTime() },
    transformRequest: isPostMethod && !opts.postJSON ? jsToURITransform : [...axios.defaults.transformRequest],
  };

  //TODO custom opts like "postJSON" don't really make sense to merge onto the config.
  // We should have a better way of handling those, perhaps simply stripping them off
  // Leaving for now since it doesn't seem to be hurting anything

  // TODO leaving jQuery extend for now as it automatically strips out undefined and null values which needs to happen.
  // replace with lodash or custom implementation
  return $.extend(true, {}, defaults, opts, { method, url, params, data });
}

/**
 * A factory method to return standard REST API functions
 * @param {Function} ajaxInstance - an axios instance
 */
function _createRestApi(ajaxInstance) {
  return {
    get(url, params, config) {
      return ajaxInstance.request(_createRequest('GET', url, params, {}, config));
    },

    post(url, postParams, params, config) {
      return ajaxInstance.request(_createRequest('POST', url, params, postParams, config));
    },
    delete(url) {
      return ajaxInstance.request(_createRequest('DELETE', url));
    },
    /**
     * To execute multiple requests in parallel, provide an array of requests. When all are complete,
     * returns an array containing the response objects in the same order they were sent.
     * @param      {array[]}     requests     array of requests
     */
    all(requests) {
      return axios.all(requests);
    },
  };
}

// Most functions should not use these directly, instead use _getToken... TODO create better encapsulation
let _token;
/**
 * Will return a JWT.
 * First checks if there is currently a valid token, if so, will return that
 * Otherwize will create a new one
 * @param {{ forceRefresh: boolean }} options - can force a new JWT to be generated
 */
function _getToken(options = {}) {
  const { forceRefresh = false } = options;
  return new Promise((resolve) => {
    if (!forceRefresh && _token) {
      resolve(_token);
    } else {
      _NGXAjax.get('/api/login/token').then((response) => {
        _token = response.data.access_token;
        resolve(_token);
      });
    }
  });
}

function _addAuthHeader(config, token) {
  return _.merge({}, config, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * transform function for turning a JSON piece of data into a request payload. To be used with axios 'transformRequest',
 * @param data - JSON object to be transformed
 * @param header - JSON object as passed in by the axios request
 * @returns {string} - URI encoded string of the data representation
 */
export const jsToURITransform = (data, header) => {
  const uri = [];
  for (var key in data) {
    if (data[key] !== null) uri.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]));
  }
  return uri.join('&');
};

/**
 * transform function for turning a JSON piece of data into a request payload.
 * @param data - JSON object to be transformed
 * @param compact - remove all params with falsey/empty values
 * @returns {string} - URI encoded string of the data representation
 */
export const jsToURICompactTransform = (data, compact) => {
  const uri = [];
  for (var key in data) {
    if (!compact || !_.isEmpty(data[key])) {
      uri.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]));
    }
  }
  return uri.join('&');
};

/**
 * @param {*} query - a string, containing key-value pairs, ex. '?firstName=John&lastName=Smith'
 * @returns an object, of key: value paris, like {firtsName: 'John', lastName: 'Smith'}
 */
export const urlParamsToObject = (query) => {
  const urlParams = new URLSearchParams(query);
  const entries = urlParams.entries();
  const result = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] pair. Note: urlParams.entries() gives back an IterableIterator, NOT an object. So we need to pick them up using the "of" keyword!!!
    result[key] = value;
  }
  return result;
};

export const NGXAjax = _NGXAjax;
window.NGXAjax = NGXAjax;
